import React, { useEffect, useState } from "react";
import { Alert, Button, Modal, Space, Typography, message as antdMessage } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { sendInvoic } from "../../../../../functions/edifact";

const { Text } = Typography;

/**
 * Handmatig versturen van een al gegenereerde INVOIC (edifactInvoicMessage op
 * de factuur, gemaakt door de INVOIC-trigger) via Descartes. Toont het ruwe bericht.
 */
const SendInvoicModal = ({ visible, invoice, onCancel, onSent }) => {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (visible) setError(null);
  }, [visible]);

  const alreadySent = !!invoice?.edifactInvoicSentAt;

  // Al verstuurd: eerst bevestigen, Descartes stuurt het anders gewoon nog een keer door.
  const confirmSend = () =>
    alreadySent
      ? Modal.confirm({
          title: t("send_invoic_again"),
          content: t("confirm_send_invoic_again"),
          okText: t("send_again"),
          cancelText: t("cancel"),
          onOk: handleSend,
        })
      : handleSend();

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const { tid } = await sendInvoic(invoice.id, COMPANY_COUNTRY_CODE);
      antdMessage.success(t("invoic_sent_successfully", { tid: tid || "-" }));
      onSent();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title={t("send_invoic_title")}
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t("cancel")}
        </Button>,
        <Button
          key="send"
          type="primary"
          loading={sending}
          disabled={!invoice?.edifactInvoicMessage}
          onClick={confirmSend}
        >
          {t(alreadySent ? "send_again" : "send")}
        </Button>,
      ]}
    >
      {invoice?.edifactInvoicMessage && (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Text type="secondary">{t("send_invoic_message")}</Text>
          {invoice.edifactInvoicSentAt && (
            <Alert
              type="warning"
              showIcon
              message={t("send_invoic_already_sent", {
                date: dayjs.unix(invoice.edifactInvoicSentAt).format("DD-MM-YYYY HH:mm"),
                tid: invoice.edifactInvoicTid || "-",
              })}
            />
          )}
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, fontSize: 12 }}>
            {invoice.edifactInvoicMessage.replace(/'/g, "'\n")}
          </pre>
          {error && <Alert type="error" showIcon message={error} />}
        </Space>
      )}
    </Modal>
  );
};

export default SendInvoicModal;
